import type { PropTypeOption } from '@components/ui/analysis-config/configTypes';
import type { AnalysisConfig } from '@views/picks/constants';
import type { ReactElement } from 'react';
import { Section } from './Section';
import { Toggle } from './Toggle';

interface PropTypesSectionProps {
  config: AnalysisConfig;
  isExpanded: boolean;
  onToggleExpand: (key: string) => void;
  getEnabledCount: (key: string) => number;
  getTotalCount: (key: string) => number;
  toggleAllInSection: (key: string, enable: boolean) => void;
  toggleCategory: (category: string, enable: boolean) => void;
  updateConfig: (section: string, key: string, value: boolean | number | string) => void;
  leaguePropTypeOptions: PropTypeOption[];
}

export function PropTypesSection({
  config,
  isExpanded,
  onToggleExpand,
  getEnabledCount,
  getTotalCount,
  toggleAllInSection,
  toggleCategory,
  updateConfig,
  leaguePropTypeOptions,
}: PropTypesSectionProps): ReactElement {
  // Group by category for visual organization (filtered by league)
  const categories: string[] = [];
  for (const opt of leaguePropTypeOptions) {
    if (!categories.includes(opt.category)) categories.push(opt.category);
  }

  return (
    <Section
      title="Prop Types"
      sectionKey="propTypes"
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      enabledCount={getEnabledCount('propTypes')}
      totalCount={getTotalCount('propTypes')}
      showToggleAll
      onToggleAll={toggleAllInSection}
      minEnabled={1}
    >
      {categories.map((cat) => (
        <div key={cat}>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-theme-text-tertiary px-1 pt-2 pb-0.5">
            <span className="font-medium">{cat}</span>
            <div className="flex gap-1.5 normal-case tracking-normal">
              <button
                type="button"
                onClick={() => toggleCategory(cat, true)}
                className="text-theme-text-tertiary hover:text-theme-primary transition"
              >
                All
              </button>
              <span className="text-theme-border">/</span>
              <button
                type="button"
                onClick={() => toggleCategory(cat, false)}
                className="text-theme-text-tertiary hover:text-theme-danger transition"
              >
                None
              </button>
            </div>
          </div>
          {leaguePropTypeOptions
            .filter((opt) => opt.category === cat)
            .map((opt) => (
              <Toggle
                key={opt.key}
                label={opt.label}
                description={opt.description}
                checked={config.propTypes?.[opt.key] ?? false}
                onChange={(v) => {
                  if (!v) {
                    const currentEnabled = Object.values(config.propTypes ?? {}).filter(
                      Boolean,
                    ).length;
                    if (currentEnabled <= 1) return;
                  }
                  updateConfig('propTypes', opt.key, v);
                }}
              />
            ))}
        </div>
      ))}
    </Section>
  );
}
